import barracks from '../../data/buildings/barracks.json';
import goldMine from '../../data/buildings/gold-mine.json';
import townCenter from '../../data/buildings/town-center.json';
import elves from '../../data/factions/elves.json';
import orcs from '../../data/factions/orcs.json';
import melee from '../../data/units/melee.json';
import ranged from '../../data/units/ranged.json';
import worker from '../../data/units/worker.json';
import type { BuildingDef, UnitDef } from '../sim/data/types';
import { parseBuildingDef } from './buildings';
import { parseFaction, type Faction } from './factions';
import { parseUnitDef } from './units';

export interface Content {
  readonly units: ReadonlyMap<string, UnitDef>;
  readonly buildings: ReadonlyMap<string, BuildingDef>;
  readonly factions: ReadonlyMap<string, Faction>;
  unit(id: string): UnitDef;
  building(id: string): BuildingDef;
  faction(id: string): Faction;
}

function must<T>(map: ReadonlyMap<string, T>, id: string, what: string): T {
  const v = map.get(id);
  if (!v) throw new Error(`Unknown ${what} '${id}'`);
  return v;
}

/** Validate every data file once at startup. Throws with a readable message on bad data. */
export function loadContent(): Content {
  const units = new Map<string, UnitDef>();
  for (const [raw, src] of [
    [worker, 'data/units/worker.json'],
    [melee, 'data/units/melee.json'],
    [ranged, 'data/units/ranged.json'],
  ] as const) {
    const def = parseUnitDef(raw, src);
    units.set(def.id, def);
  }
  const buildings = new Map<string, BuildingDef>();
  for (const [raw, src] of [
    [townCenter, 'data/buildings/town-center.json'],
    [goldMine, 'data/buildings/gold-mine.json'],
    [barracks, 'data/buildings/barracks.json'],
  ] as const) {
    const def = parseBuildingDef(raw, src);
    buildings.set(def.id, def);
  }
  const factions = new Map<string, Faction>();
  for (const [raw, src] of [
    [orcs, 'data/factions/orcs.json'],
    [elves, 'data/factions/elves.json'],
  ] as const) {
    const f = parseFaction(raw, src);
    factions.set(f.id, f);
  }
  return {
    units,
    buildings,
    factions,
    unit: (id) => must(units, id, 'unit'),
    building: (id) => must(buildings, id, 'building'),
    faction: (id) => must(factions, id, 'faction'),
  };
}
